/**
 * Textos de `admin` en español — la fuente. `en/admin.ts` tiene que tener
 * exactamente las mismas claves (lo exige el tipo), así que una clave nueva
 * acá rompe el typecheck hasta que alguien la traduce.
 *
 * Interpolación con funciones, no con plantillas "{n}": el compilador revisa
 * los argumentos y cada idioma arma su propia gramática (plurales, orden).
 *
 * La consola de operación estaba mezclada (español con restos en inglés como
 * "Bounce rate" o "in / out"). Lo que ya estaba en español se copió tal cual;
 * lo que estaba en inglés se pasó a un español natural. "Workers" y "Gate" se
 * quedan así a propósito: es como el equipo nombra esas piezas de la máquina.
 */
const admin = {
  /** Encabezado común de las pantallas (el `scope` de PageHeader). */
  scope: 'Operación',
  /** Paginación de las tablas. */
  pagination: {
    page: (n: number) => `Página ${n}`,
    pageOf: (n: number, pages: number) => `Página ${n} de ${pages}`,
    range: (from: number, to: number, total: number) => `${from} a ${to} de ${total}`,
  },
  /** Celda vacía. */
  empty: '—',
  layout: {
    loading: 'Cargando la consola',
    gateTitle: 'Esta sección es de operación',
    backToDashboard: 'Volver a mi panel',
    gateBody:
      'Tu cuenta no tiene permisos de operador. Si crees que debería tenerlos, pídeselos a quien administra la plataforma.',
    brandSuffix: 'Operación',
    navLabel: 'Secciones de operación',
    nav: {
      overview: 'Resumen',
      users: 'Usuarios',
      jobs: 'Ofertas',
      agencies: 'Agencias',
      partners: 'Socios',
      workers: 'Workers',
      english: 'Inglés',
    },
  },
  overview: {
    title: 'Resumen',
    rangeLabel: 'Rango',
    range: {
      d7: '7 días',
      d30: '30 días',
      all: 'Todo',
    },
    kpi: {
      sent: 'Enviadas',
      replyRate: 'Tasa de respuesta',
      interviewRate: 'Tasa de entrevistas',
      queued: 'En cola',
      discardRate: 'Postulaciones descartadas',
      realBounce: (pct: string) => `Rebote real ${pct}`,
      topReason: (reason: string) => `Más común: ${reason}`,
      gateFailRate: 'Tasa de fallo del gate',
      tokenCost: 'Costo de tokens (USD)',
      tokens: (input: string, output: string) => `${input} de entrada / ${output} de salida`,
    },
    funnelTitle: 'Embudo',
    funnel: {
      drafts: 'Borradores',
      queued: 'En cola',
      sent: 'Enviadas',
      replied: 'Respondidas',
      interviews: 'Entrevistas',
    },
    trendTitle: 'Enviadas y respondidas',
    trendEmpty: 'Sin envíos en el rango elegido.',
    trendSent: 'Enviadas',
    trendReplied: 'Respondidas',
    workersTitle: 'Workers',
    never: 'nunca',
    lede: 'Arriba el negocio, abajo la operación de envío. Sin datos demo ni cuentas del equipo.',
    /** Lo que comparten las dos gráficas de tendencia. */
    chart: {
      legend: 'Series y total del rango',
      keyboardHint: 'Usa las flechas para recorrer las fechas.',
      showTable: 'Ver como tabla',
      date: 'Fecha',
      week: 'Semana',
      weekOf: (date: string) => `Semana del ${date}`,
    },
    activity: {
      scope: 'Quién entra y qué lo trae de vuelta',
      title: 'Actividad',
      error: 'No se pudo cargar la actividad. El resto del Resumen sí cargó.',
      truncated: 'Hay más datos de los que caben; lo más viejo quedó fuera.',
      trackingSince: (date: string) => `Visitas desde el ${date}`,
      trackingPending: 'Las visitas se empiezan a contar desde el próximo ingreso.',
      kpi: {
        today: 'Activos hoy',
        d7: 'Activos en 7 días',
        d30: 'Activos en 30 días',
        stickiness: 'Vuelven',
        stickinessDesc: 'Promedio diario de activos sobre los activos de 30 días',
        noData: 'sin datos',
      },
      trend: {
        title: 'Quién entra cada día',
        active: 'Activos',
        new: 'Nuevos',
        returning: 'Recurrentes',
        plansTitle: 'Activos por plan',
        free: 'Sin plan',
        trial: 'En prueba',
        plan: 'Con plan',
        empty: 'Sin actividad en el rango elegido.',
      },
      activation: {
        scope: 'Primera semana',
        title: 'Activación',
        empty: 'Todavía no hay registros en el rango.',
        noDataYet: 'sin datos aún',
        ofLine: (reached: string, of: string, pct: string) => `${reached} de ${of} · ${pct}`,
        signedInNoMembership: (n: string) => `Iniciaron sesión y no empezaron: ${n}`,
        steps: {
          returned: 'Volvieron',
          has_cv: 'Subieron su hoja de vida',
          jobs_browsed: 'Vieron ofertas',
          preferences_saved: 'Guardaron preferencias',
          applied: 'Postularon',
          trial: 'Empezaron la prueba',
          paid: 'Pagaron',
        } as Record<string, string>,
      },
      apps: {
        scope: 'App móvil',
        title: 'TBit Jobs en el celular',
        trackingSince: (date: string) => `Plataforma medida desde el ${date}. Cuenta a quien abre la app con sesión; las descargas de la tienda están en App Store Connect y Play Console.`,
        trackingPending: 'La plataforma se empieza a contar con el próximo uso de la app.',
        kpi: {
          users: 'Con la app',
          usersDesc: (ios: string, android: string) => `iPhone ${ios} · Android ${android}`,
          active: 'Activos en la app, 7 días',
          activeDesc: (ios: string, android: string) => `iPhone ${ios} · Android ${android}`,
          share: 'Usan la app',
          shareDesc: 'De los activos de 7 días, cuántos la usaron',
          noData: 'sin datos',
        },
        installsTitle: 'Nuevos en la app',
        activeTitle: 'Activos por plataforma',
        web: 'Web',
        ios: 'iPhone',
        android: 'Android',
        empty: 'Todavía nadie usa la app en el rango elegido.',
        versionsTitle: 'Versiones en uso (7 días)',
        versionLine: (platform: string, version: string, people: string) => `${platform} ${version}: ${people}`,
      },
      notifications: {
        scope: 'Avisos',
        title: 'Qué tanto trae de vuelta cada aviso',
        empty: 'Todavía no sale ningún aviso medido.',
        columns: {
          tag: 'Aviso',
          sent: 'Enviados',
          people: 'Personas',
          returned: 'Volvieron en 48 h',
          viaLink: 'Entraron por el enlace',
          acted: 'Hicieron algo',
        },
        returnedCell: (n: string, of: string, pct: string) => `${n} de ${of} (${pct})`,
        tags: {
          agency_invitation: 'Invitación de agencia',
          employer_reply: 'Respuesta de un empleador',
          payment_failed: 'Pago fallido',
          english_requested: 'Prueba de inglés pedida',
          english_reminder: 'Recordatorio de inglés',
          english_retry: 'Reintento de inglés',
          english_result: 'Resultado de inglés',
        } as Record<string, string>,
      },
      usage: {
        scope: 'Uso',
        pagesTitle: 'Páginas más vistas',
        actionsTitle: 'Acciones más hechas',
        empty: 'Sin datos todavía.',
        line: (people: string) => `${people} personas`,
        pages: {
          panel: 'Panel',
          postulaciones: 'Postulaciones',
          mensajes: 'Mensajes',
          'mensajes/:id': 'Un mensaje',
          perfil: 'Perfil',
          'perfil/hoja-de-vida': 'Hoja de vida',
        } as Record<string, string>,
        actions: {
          cv_uploaded: 'Subió su hoja de vida',
          cv_written: 'Escribió su hoja de vida',
          profile_edited: 'Editó su perfil',
          jobs_browsed: 'Vio ofertas',
          applied_manual: 'Postuló a mano',
          drafts_generated: 'Generó borradores',
          demo_sent: 'Envió la demo',
          auto_send_activated: 'Activó el envío',
          checkout_opened: 'Abrió el pago',
          preferences_saved: 'Guardó preferencias',
          talent_pool: 'Entró a la bolsa de talento',
          message_replied: 'Respondió un mensaje',
          invitation_answered: 'Contestó una invitación',
          payment_recovered: 'Recuperó un pago',
        } as Record<string, string>,
      },
    },
    business: {
      scope: 'Negocio',
      title: 'Cómo va TBit Jobs',
      note: 'Sin datos demo, cuentas del equipo ni operadores. «Pagando», «En prueba» y «Cortesía» usan la misma regla que el panel de socios.',
      error: 'No se pudo cargar el negocio. La operación de abajo sí cargó.',
      review: 'Revisar',
      /** El valor de una baldosa que depende de Stripe cuando Stripe no contestó. */
      noStripeValue: 'Sin dato',
      versus: {
        d7: 'vs. los 7 días anteriores',
        d30: 'vs. los 30 días anteriores',
      },
      delta: {
        up: (pct: string) => `▲ ${pct}`,
        down: (pct: string) => `▼ ${pct}`,
        same: 'Igual',
        fromZero: '▲ desde cero',
      },
      kpi: {
        registrations: 'Registros nuevos',
        withCv: 'Con hoja de vida',
        trials: 'Pruebas iniciadas',
        paying: 'Pagando ahora',
        courtesies: 'Cortesías vigentes',
        cancellations: 'Cancelaciones',
        abandoned: 'Checkouts abandonados',
        mrr: 'Ingreso mensual (MRR)',
      },
      desc: {
        ofRegistrations: (pct: string) => `${pct} de los registros del rango`,
        inTrialNow: (n: number) => `${n} en prueba hoy`,
        firstCharges: (n: number) => (n === 1 ? '1 primer cobro en el rango' : `${n} primeros cobros en el rango`),
        today: 'Hoy',
        cancellationsSplit: (paid: number, trial: number) => `${paid} pagaban · ${trial} en prueba`,
        abandonedSessions: (n: number) => (n === 1 ? '1 sesión sin pago' : `${n} sesiones sin pago`),
        abandonedTruncated: 'Puede haber más: Stripe se leyó hasta un tope',
        noStripe: 'Sin datos de Stripe',
        mrrPaying: (n: number) => (n === 1 ? '1 pagando' : `${n} pagando`),
        mrrListPrice: (n: number) => `${n} a precio de lista`,
        mrrUnpriced: (n: number) => `${n} sin precio mensual`,
      },
      funnel: {
        scope: 'Registros del rango',
        title: 'Embudo de conversión',
        registered: 'Registro',
        withCv: 'Hoja de vida',
        trial: 'Prueba iniciada',
        firstCharge: 'Primer cobro',
        step: (pct: string) => `${pct} del paso anterior`,
        ofRegistered: (pct: string) => `${pct} del registro`,
        empty: 'Nadie se registró en este rango.',
      },
      origin: {
        scope: 'Registros del rango',
        title: 'Origen',
        columns: { origin: 'Origen', registered: 'Registros', trial: 'Prueba', paying: 'Pago' },
        labels: { direct: 'Directo', partner: 'Socio', coupon: 'Cupón', courtesy: 'Cortesía' },
        topPartners: 'Socios que más traen',
        noPartners: 'Ningún registro con código de socio en este rango.',
        partnerLine: (registered: string, trial: string, paying: string) =>
          `${registered} registros · ${trial} en prueba · ${paying} pagaron`,
      },
      trend: {
        title: 'Registros, pruebas y primeros cobros',
        byDay: 'Por día',
        byWeek: 'Por semana',
        registrations: 'Registros',
        trials: 'Pruebas',
        firstCharges: 'Primeros cobros',
        empty: 'Sin movimiento en el rango.',
      },
      partners: {
        scope: 'Programa de socios',
        title: 'Socios',
        active: 'Activos',
        pending: 'Por aprobar',
        referrals: 'Referidos',
        referralsRange: (n: string) => `${n} en el rango`,
        paid: 'Comisiones pagadas',
        due: 'Por pagar',
        failed: (usd: string) => `${usd} con transferencia fallida`,
      },
      agencies: {
        scope: 'Empleadores',
        title: 'Agencias',
        new: 'Nuevas',
        total: 'En total',
        active: 'Activas',
        pending: 'Por aprobar',
        openPositions: 'Vacantes abiertas',
      },
      recent: {
        scope: 'Consola de operador',
        title: 'Últimos registros',
        columns: { date: 'Fecha', email: 'Correo', origin: 'Origen', plan: 'Plan', cv: 'Hoja de vida' },
        cvYes: 'Sí',
        cvNo: 'No',
        empty: 'Todavía no hay registros.',
        state: {
          paying: 'Pagando',
          trial: 'En prueba',
          no_trial: 'Sin prueba activa',
          canceled: 'Canceló',
          courtesy: 'Cortesía',
        },
      },
    },
    ops: {
      scope: 'Operación',
      title: 'Envío de postulaciones',
      qualityLast30: 'Últimos 30 días',
      ofPrevious: (pct: string) => `${pct} de la etapa anterior`,
    },
  },
  jobs: {
    title: 'Ofertas de empleo',
    syncing: 'Sincronizando…',
    syncDol: 'Sincronizar DOL',
    searchPlaceholder: 'Buscar cargo, empresa, ubicación',
    status: {
      any: 'Estado',
      active: 'Activas',
      held: 'Retenidas (Canadá)',
      archived: 'Archivadas',
    },
    source: {
      any: 'Fuente',
      dolH2b: 'DOL H2B',
      dolH2a: 'DOL H2A',
      csv: 'CSV',
      manual: 'Manual',
      jobbank: 'Job Bank (Canadá)',
      euresOpen: 'EURES abiertas (fuera de la UE)',
      euresCodc: 'EURES oficios en escasez (España)',
      eures: 'EURES (con papeles UE)',
    },
    columns: {
      title: 'Cargo',
      company: 'Empresa',
      location: 'Ubicación',
      source: 'Fuente',
      status: 'Estado',
      assigned: 'Asignados',
      applications: 'Postulaciones',
      gate: 'Gate',
      created: 'Creada',
    },
    archive: 'Archivar',
    reactivate: 'Reactivar',
    emptyTitle: 'Sin ofertas',
    emptyNote: 'Sincroniza DOL o crea una manual.',
  },
  users: {
    title: 'Usuarios',
    invite: '+ Invitar operador',
    /**
     * Las tarjetas de arriba: toda la base de candidatos (sin el equipo), y al
     * hacer clic filtran la tabla. «Activo» solo no dice si a alguien le sale
     * algo: puede estar activo y sin plan.
     */
    stats: {
      total: 'Cuentas',
      totalDesc: 'Candidatos registrados',
      sending: 'Enviando',
      sendingDesc: 'Con plan y envío encendido',
      trial: 'En prueba',
      trialDesc: 'Los 7 días gratis en curso',
      noPlan: 'Activos sin plan',
      noPlanDesc: 'No les sale nada',
      stopped: 'Detenidos',
      stoppedDesc: 'Pausados o desconectados',
      filterHint: 'Clic para ver solo estas cuentas; otro clic quita el filtro.',
    },
    searchPlaceholder: 'Buscar por correo o nombre',
    referral: {
      any: 'Referidos',
      onlyReferred: 'Solo referidos',
      byCode: (code: string) => `Referidos de ${code}`,
      /** Debajo del correo: el socio que la trajo, su código y su comunidad. */
      by: (name: string | null, code: string, community: string | null) =>
        `Referido por ${name ? `${name} (${code})` : code}${community ? ` · ${community}` : ''}`,
      filterHint: 'Clic para ver todos los referidos de este socio.',
    },
    status: {
      any: 'Estado',
      active: 'Activo',
      paused: 'Pausado',
      disconnected: 'Desconectado',
    },
    role: {
      any: 'Rol',
      user: 'Usuario',
      operator: 'Operador',
    },
    columns: {
      email: 'Correo',
      created: 'Registro',
      status: 'Estado',
      role: 'Rol',
      plan: 'Plan',
      visa: 'Visa',
      today: 'Hoy',
      total: 'Total',
      replies: 'Respuestas',
      limit: 'Límite',
      lastSent: 'Último envío',
      access: 'Acceso',
      cv: 'Hoja de vida',
    },
    /** La columna Hoja de vida: el puntaje 0-100 que ve el candidato en su panel. */
    cv: {
      none: 'Sin hoja de vida',
      unscored: 'Sin puntaje',
      scoredAt: (date: string) => `Puntuada el ${date}`,
    },
    visaBoth: 'Ambas',
    /**
     * La columna Acceso: si activó la prueba, si paga, y qué le falta. El
     * avance de muestras es lo que el candidato se mandó a su propio correo.
     */
    access: {
      trial: (date: string) => (date ? `En prueba hasta el ${date}` : 'En prueba'),
      paying: 'Pagando',
      courtesy: (date: string) => (date ? `Cortesía hasta el ${date}` : 'Cortesía'),
      canceled: 'Canceló',
      neverTried: 'No activó la prueba',
      trialUnpaid: 'Probó y no pagó',
      demos: (n: number) => `${n} de 3 muestras abiertas`,
      sendOff: 'Envío apagado',
      sendOffHint: 'Tiene plan, pero no le dio «Empezar a enviar»: no le sale nada a ningún empleador.',
    },
    actions: {
      pause: 'Pausar',
      resume: 'Reanudar',
      removeOperator: 'Quitar operador',
      promoteOperator: 'Promover a operador',
      comp: 'Suscripción de cortesía',
      checkoutTitle: 'Generar checkout',
      viewDetail: 'Ver detalle',
      viewAs: 'Ver como',
      viewAsHint: 'Abre su app tal como la ve, en una pestaña nueva y sin poder cambiar nada.',
      platform: 'Platform',
      platformHint: 'Abre su chat en el agente TBit Jobs de platform.tbit.app, en una pestaña nueva.',
      platformMissing: 'Todavía no está en el agente TBit Jobs de la plataforma: la sincronización corre cada 2 minutos.',
      publicProfile: 'Perfil público',
      publicProfileHint: 'Abre su hoja pública en jobs.tbit.app/perfiles, en una pestaña nueva.',
    },
    viewAsError: 'No se pudo abrir la vista de esta persona.',
    noResults: 'Sin resultados.',
    checkoutCopied: (url: string) => `URL de checkout copiada al portapapeles:\n${url}`,
    checkoutError: (message: string) => `Error generando checkout: ${message}`,
    checkoutTermsRequired:
      'No se generó el enlace: esta persona todavía no aceptó la versión vigente de los Términos y condiciones de TBit Jobs. Pídele que entre a jobs.tbit.app y la acepte al empezar su prueba o pagar.',
  },
  agencies: {
    title: 'Agencias',
    lede: 'Aprueba el acceso a candidatos y administra el estado de cada agencia.',
    searchPlaceholder: 'Buscar agencia o correo',
    filterLabel: 'Estado de la agencia',
    filter: {
      all: 'Todas',
      pendingAccess: 'Pendientes de acceso',
      pending: 'Pendientes',
      active: 'Activas',
      suspended: 'Suspendidas',
    },
    columns: {
      name: 'Nombre',
      createdBy: 'Creada por',
      created: 'Creada',
      openPositions: 'Vacantes abiertas',
      seats: 'Asientos',
      status: 'Estado',
      accessRequested: 'Pidió acceso',
    },
    website: 'Sitio web',
    actions: {
      approve: 'Aprobar',
      suspend: 'Suspender',
      reactivate: 'Reactivar',
    },
    confirm: '¿Confirmar?',
    yes: 'Sí',
    no: 'No',
    emptyTitle: 'Sin agencias',
    emptyNote: 'No hay agencias para este filtro.',
  },
  workers: {
    title: 'Workers',
    rel: {
      never: 'nunca',
      now: 'ahora',
      minutes: (n: number) => `hace ${n} min`,
      hours: (n: number) => `hace ${n} h`,
      days: (n: number) => `hace ${n} d`,
    },
    /** Nombre legible de cada worker; el `name` técnico va en el scope. */
    names: {
      send: 'Envío de aplicaciones',
      dolSync: 'Sync de ofertas DOL',
      bounceScanner: 'Detector de bounces',
      replyScanner: 'Detector de respuestas',
      jobBankSync: 'Caducidad de Job Bank',
      jobBankDiscovery: 'Descubrimiento de Job Bank',
      euresSync: 'Caducidad de EURES',
      euresDiscovery: 'Descubrimiento de EURES',
    } as Record<string, string>,
    lastRun: 'Última ejecución:',
    duration: 'Duración:',
    durationMs: (ms: number) => `${ms} ms`,
    processed: 'Procesados:',
    failed: 'Fallidos:',
    staleThreshold: (min: number) => `Umbral stale: ${min} min`,
    lastError: 'Último error',
  },
  compModal: {
    title: 'Suscripción de cortesía',
    plan: 'Plan',
    months: 'Meses',
    /** Por qué se concede: obligatorio. «Pagó por fuera» exige el monto. */
    reason: 'Motivo',
    reasonPlaceholder: 'Elige un motivo',
    reasons: {
      paid_offline: 'Pagó por fuera',
      courtesy: 'Cortesía',
      compensation: 'Compensación',
    },
    amount: 'Monto pagado (USD)',
    reasonRequired: 'Elige el motivo de la cortesía.',
    amountRequired: 'Escribe cuánto pagó, en USD (mayor que 0).',
    note: 'Nota (opcional)',
    cancel: 'Cancelar',
    grant: 'Conceder',
  },
  inviteModal: {
    title: 'Invitar operador',
    body: 'Crea un acceso de operador para un correo externo. Si aún no se ha registrado, obtendrá acceso al iniciar sesión por primera vez.',
    emailPlaceholder: 'email@dominio.com',
    cancel: 'Cancelar',
    create: 'Crear',
  },
  drawer: {
    close: '← Cerrar',
    loadError: 'No se pudo cargar.',
    autoSend: (on: boolean) => `Envío automático: ${on ? 'sí' : 'no'}`,
    actionsTitle: 'Acciones de admin',
    busy: '…',
    pauseUser: 'Pausar usuario',
    resumeUser: 'Reanudar usuario',
    stopSending: 'Detener envíos',
    startSending: 'Activar envíos',
    grantSubscription: 'Conceder suscripción',
    removeOperator: 'Quitar operador',
    promoteOperator: 'Promover a operador',
    periodTitle: 'Cambiar período de suscripción',
    periodStart: 'Inicio',
    periodEnd: 'Vencimiento',
    applyPeriod: 'Aplicar período',
    discard: 'Descartar',
    periodHint:
      'Deja una fecha vacía para limpiarla. Vencimiento controla cuándo el usuario pierde el plan.',
    preferences: 'Preferencias',
    voiceProfile: 'Perfil de voz',
    compHistory: 'Historial de cortesías',
    compEntry: (plan: string, granted: string, expires: string) =>
      `${plan} · concedido ${granted} · expira ${expires}`,
    /** Entradas viejas, de antes de que el motivo fuera obligatorio. */
    compNoReason: 'Sin motivo',
    compAmount: (amount: string) => `USD ${amount}`,
    recentApplications: 'Postulaciones recientes',
  },
  /** `AdminPartnersPage`: la bandeja del Programa de socios. */
  partners: {
    title: 'Socios',
    lede: 'Aprueba o rechaza solicitudes al Programa de socios. El monto es fijo: USD 7 de descuento al referido en su primer mes y USD 5 por mes pagado al socio. Pausar o terminar no frena lo ya causado: se le sigue pagando. Para frenar la plata está «Retener pagos».',
    filterLabel: 'Estado del socio',
    filter: {
      all: 'Todos',
      pending: 'Pendientes',
      active: 'Activos',
      paused: 'Pausados',
      rejected: 'Rechazados',
      terminated: 'Terminados',
    },
    columns: {
      partner: 'Socio',
      code: 'Código',
      promotion: 'Cómo va a promocionar',
      referrals: 'Referidos',
      commissions: 'Comisiones',
      payouts: 'Cobros',
      status: 'Estado',
    },
    payouts: { none: 'Sin configurar', pending: 'Pendiente', ready: 'Listo' },
    commissions: (paid: string, pending: string) => `${paid} pagado · ${pending} por pagar`,
    actions: {
      approve: 'Aprobar',
      reject: 'Rechazar',
      pause: 'Pausar',
      reactivate: 'Reactivar',
      terminate: 'Terminar',
      hold: 'Retener pagos',
      release: 'Soltar pagos',
    },
    confirm: '¿Seguro?',
    yes: 'Sí',
    no: 'No',
    rejectReason: 'Motivo del rechazo (se lo enviamos por correo)',
    rejectSend: 'Rechazar con este motivo',
    cancel: 'Cancelar',
    rejectedBecause: (reason: string) => `Motivo: ${reason}`,
    /** Terminar es definitivo: lo causado se le paga igual (salvo retención). */
    terminateReason: 'Motivo de la terminación (queda registrado)',
    terminateSend: 'Terminar con este motivo',
    /** Retener frena TODA transferencia al socio, en cualquier estado, hasta soltarla. */
    holdReason: 'Motivo de la retención (sospecha de fraude o incumplimiento)',
    holdSend: 'Retener con este motivo',
    holdInFlight: (n: number) =>
      `Pagos retenidos. ${n === 1 ? 'Una transferencia ya estaba saliendo' : `${n} transferencias ya estaban saliendo`} y la retención no la${n === 1 ? '' : 's'} frena: revísala${n === 1 ? '' : 's'} en Stripe y revierte si hace falta.`,
    terminatedOn: (date: string, reason: string) => `Terminado el ${date}. Motivo: ${reason}`,
    held: (date: string, reason: string, by: string | null) =>
      `Pagos retenidos el ${date}${by ? ` por ${by}` : ''}. Motivo: ${reason}`,
    termsVersion: (version: string) => `Términos v. ${version}`,
    errors: {
      codeTaken: 'Ese código ya existe en Stripe. Pídele al socio que elija otro.',
      invalidTransition: 'El socio cambió de estado. Recarga la página.',
      stripe: 'Stripe no respondió. Inténtalo de nuevo.',
      failed: 'No se pudo completar la acción.',
    },
    emptyTitle: 'Sin solicitudes',
    emptyNote: 'Cuando alguien pida entrar al programa, aparece acá y te llega un correo.',
  },
  /**
   * Etiqueta visible de cada estado/rol del semáforo. El valor técnico (lo que
   * guarda el servidor) no cambia; si llega uno que no está acá, se muestra tal
   * cual.
   */
  status: {
    ACTIVE: 'Activo',
    PENDING: 'Pendiente',
    SUSPENDED: 'Suspendido',
    PAUSED: 'Pausado',
    DISCONNECTED: 'Desconectado',
    ARCHIVED: 'Archivado',
    HELD: 'Retenido',
    OPERATOR: 'Operador',
    USER: 'Usuario',
    ok: 'OK',
    stale: 'Atrasado',
    error: 'Error',
    unknown: 'Desconocido',
    DRAFT: 'Borrador',
    QUEUED: 'En cola',
    SENT: 'Enviada',
    REPLIED: 'Respondida',
    INTERVIEW: 'Entrevista',
    REJECTED: 'Rechazada',
    TERMINATED: 'Terminado',
  } as Record<string, string>,
};

export default admin;
